<template>
  <!-- No backdrop-click close: a stray click outside must not discard a
       part-typed email. Close with the X button or Escape. -->
  <div class="auth-modal-backdrop">
    <div class="auth-modal-container">
      <div class="auth-modal-header">
        <h2 class="auth-modal-title">
          <i class="fas fa-user-plus"></i>
          {{ $t('auth.register.title') || 'Create Account' }}
        </h2>
        <button class="auth-modal-close" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <form @submit.prevent="handleRegister" class="auth-form">
        <div v-if="invitationToken" class="info-message">
          <i class="fas fa-envelope-open-text"></i>
          <span>
            You've been invited to join an organization. Sign up to accept
            — we'll surface the invitation right after you activate your account.
          </span>
        </div>
        <div v-if="errorMessage" class="error-message">
          <i class="fas fa-exclamation-circle"></i>
          {{ errorMessage }}
        </div>

        <div class="form-group">
          <label for="email">
            <i class="fas fa-envelope"></i>
            {{ $t('auth.register.email') || 'Email' }}
          </label>
          <input
            id="email"
            v-model="form.email"
            type="email"
            required
            :disabled="loading"
            :placeholder="$t('auth.register.emailPlaceholder') || 'you@example.com'"
            autocomplete="email"
            class="form-input"
          >
          <p class="form-hint">
            You'll sign in with this address. No password yet — you choose one
            when you open the activation email.
          </p>
        </div>

        <!-- W4 — trial-terms summary. Rendered from the live trial-enabled
             plan's trial block when available; hidden when the trial block
             isn't shipped yet (graceful degrade — signup still works). -->
        <div v-if="trialSummary" class="trial-summary">
          <i class="fas fa-gift"></i>
          <span>{{ trialSummary }}</span>
        </div>

        <div class="form-terms">
          <label class="checkbox-label">
            <input
              v-model="form.acceptTerms"
              type="checkbox"
              required
              :disabled="loading"
            >
            <span>
              {{ $t('auth.register.acceptTerms') || 'I agree to the' }}
              <!--
                Paddle merchant review: link to the live /terms and /privacy
                routes (not the legacy /legal/* paths) so the reviewer can
                actually open them. target="_blank" + rel="noopener" so the
                signup form keeps its state when the user reads either doc.
              -->
              <a href="/terms" target="_blank" rel="noopener noreferrer" class="terms-link">
                {{ $t('auth.register.termsOfService') || 'Terms of Service' }}
              </a>
              {{ $t('auth.register.and') || 'and' }}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" class="terms-link">
                {{ $t('auth.register.privacyPolicy') || 'Privacy Policy' }}
              </a>
            </span>
          </label>
        </div>

        <!-- Human Verification -->
        <Captcha 
          ref="captcha"
          @verified="handleCaptchaVerified"
          @failed="handleCaptchaFailed"
          v-if="form.acceptTerms"
        />

        <button
          type="submit"
          :disabled="loading || !isFormValid || !captchaVerified"
          class="btn btn-primary btn-block"
        >
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <span v-else>
            <i class="fas fa-paper-plane"></i>
            Send activation email
          </span>
        </button>

        <div class="auth-footer">
          <p>
            {{ $t('auth.register.haveAccount') || 'Already have an account?' }}
            <a href="#" @click.prevent="$emit('show-login')" class="auth-link">
              {{ $t('auth.register.signIn') || 'Sign in' }}
            </a>
          </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { authService } from '@/services/auth-api'
import { clusterApi } from '@/services/cluster-api'
import Captcha from '@/components/Captcha.vue'

export default {
  name: 'RegisterModal',
  components: {
    Captcha
  },
  emits: ['close', 'show-login'],
  data() {
    return {
      // W4 — live trial terms, used to show a summary and to record
      // acceptance with the correct plan_version. Null until loaded (and
      // stays null when the backend hasn't shipped the trial block yet).
      trialTerms: null,
      // Email + the Terms/Privacy acceptance, and nothing else. Signup sends
      // no password (auth-api creates the account un-signable; the password is
      // set once, at activation). The acceptance tick stays: it is a Paddle
      // merchant-of-record prerequisite, not form decoration.
      form: {
        email: '',
        acceptTerms: false
      },
      loading: false,
      errorMessage: '',
      captchaVerified: false,
      captchaData: null,
      // Phase G v1.2 — when present (either via ?invitation_token=... or
      // persisted in localStorage by InvitationAccept.vue), surface a banner
      // so the user knows their signup is invitation-driven. Backend matches
      // the invite to the email at activation time, so no extra wiring on
      // submit is required for v1.2.
      invitationToken: ''
    }
  },
  mounted() {
    const fromQuery = this.$route?.query?.invitation_token
    let stored = ''
    try { stored = localStorage.getItem('cs:invitation-token') || '' } catch {}
    this.invitationToken = fromQuery || stored || ''
    // Pre-fill the email field when App.vue's invitation lookup
    // resolved a target email — saves the invitee a typo on the email
    // they were invited under (must match for activation).
    const ctxEmail = this.$store?.state?.invitationContext?.email
    if (ctxEmail && !this.form.email) {
      this.form.email = ctxEmail
    }
    // W4 — load the live trial terms (best-effort). Drives the trial summary
    // banner and supplies the plan_version recorded on acceptance.
    clusterApi.getTrialTerms()
      .then((terms) => { this.trialTerms = terms || null })
      .catch(() => { this.trialTerms = null })
  },
  computed: {
    // W4 — short human summary of the trial composed from the live periods.
    // Empty string when the trial block isn't available so the banner hides.
    trialSummary() {
      const t = this.trialTerms
      if (!t || t.enabled === false || !t.trial_period_human) return ''
      let s = `Your free trial lasts ${t.trial_period_human}`
      if (t.grace_period_human) {
        s += ` — after it ends we keep your data for ${t.grace_period_human} before deletion`
      }
      s += '.'
      return s
    },
    isFormValid() {
      const email = (this.form.email || '').trim()
      return Boolean(
        email.includes('@') &&
        email.includes('.') &&
        this.form.acceptTerms
      )
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    
    handleCaptchaVerified(captchaData) {
      this.captchaVerified = true
      this.captchaData = captchaData
      this.errorMessage = ''
      console.log('Registration captcha verified:', captchaData)
    },
    
    handleCaptchaFailed(failureData) {
      this.captchaVerified = false
      this.captchaData = null
      this.errorMessage = 'Too many incorrect captcha attempts. Please refresh the page and try again.'
      console.log('Registration captcha failed:', failureData)
      
      this.showNotification({
        type: 'error',
        message: 'Human verification failed. Please try again.'
      })
    },
    
    async handleRegister() {
      this.errorMessage = ''
      
      // Client-side validation
      if (!this.form.email || !this.form.email.trim()) {
        this.errorMessage = 'Please enter your email address.'
        return
      }
      
      if (!this.form.email.includes('@') || !this.form.email.includes('.')) {
        this.errorMessage = 'Please enter a valid email address.'
        return
      }
      
      if (!this.form.acceptTerms) {
        this.errorMessage = 'Please accept the Terms of Service and Privacy Policy.'
        return
      }

      if (!this.captchaVerified) {
        this.errorMessage = 'Please complete the human verification first.'
        return
      }
      
      this.loading = true
      
      try {
        // Email only. The backend defaults user_type to STARTER and creates
        // the account with NO password — activation is what gives it one, so
        // the one password policy is enforced in one place.
        // TODO (Phase G follow-up): migrate PERSONAL/COMPANY entity_type onto
        // the org draft for non-starter tiers. Profile fields (first/last
        // name, company) are collected from the profile page after
        // activation; the registration endpoint does not accept them, so the
        // form no longer asks for them.
        await authService.register({
          email: this.form.email
        })

        // W4 — record trial-terms acceptance against auth-api (the user
        // ticked "I agree" above). Best-effort: acceptTrialTerms swallows a
        // 404/501 (endpoint not live yet) and returns rather than throwing,
        // so registration is never blocked on it. INTEGRATION POINT: auth-api
        // ships POST /auth/trial-terms/accept on a parallel branch.
        try {
          await authService.acceptTrialTerms({
            planVersion: this.trialTerms?.plan_version ?? null
          })
        } catch (e) {
          console.warn('Trial-terms acceptance (best-effort) failed:', e?.message || e)
        }

        this.showNotification({
          type: 'success',
          message: 'Account created. Check your email for the activation link — you set your password there.'
        })
        
        this.$emit('close')
        
        // Show login modal or redirect to login
        this.$emit('show-login')
        
      } catch (error) {
        console.error('Registration error:', error)
        
        // Handle API validation errors properly
        let errorMessage = 'Failed to create account. Please try again.'
        
        if (error.response?.status === 409) {
          errorMessage = 'An account with this email already exists. Please use a different email or try logging in.'
        } else if (error.response?.status === 400 || error.response?.data?.message === 'Validation errors') {
          // Handle validation errors from API
          const validationErrors = error.response.data?.data
          if (validationErrors && typeof validationErrors === 'object') {
            const fieldErrors = Object.values(validationErrors)
            errorMessage = fieldErrors.length > 0 ? fieldErrors.join('. ') : 'Please check your input.'
          } else {
            errorMessage = error.response.data?.message || 'Invalid registration data. Please check your input.'
          }
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        }
        
        this.errorMessage = errorMessage
        
        this.showNotification({
          type: 'error',
          message: errorMessage
        })
        
        // Reset captcha on error to require re-verification
        this.captchaVerified = false
        this.captchaData = null
        if (this.$refs.captcha) {
          this.$refs.captcha.reset()
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.auth-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.auth-modal-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideIn 0.3s ease;
}

.auth-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.auth-modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.auth-modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.auth-modal-close:hover {
  color: #1f2937;
}

.auth-form {
  padding: 24px;
}

.error-message {
  background-color: #fee;
  color: #dc2626;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
}

.info-message {
  background-color: #eff6ff;
  color: #1e3a8a;
  border: 1px solid #c7d2fe;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.875rem;
  line-height: 1.45;
}

.info-message i {
  color: #1a7fd4;
  margin-top: 2px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  font-size: 0.875rem;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #1a7fd4;
  box-shadow: 0 0 0 3px rgba(26, 127, 212, 0.1);
}

.form-input:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

/* Says why the form ends here. Quiet on purpose — it answers a question the
   visitor may not have asked, so it must not compete with the field. */
.form-hint {
  margin: 8px 0 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: #6b7280;
}

.trial-summary {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.85rem;
  line-height: 1.45;
}

.trial-summary i {
  color: #16a34a;
  margin-top: 2px;
}

.form-terms {
  margin-bottom: 24px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
}

.checkbox-label input[type="checkbox"] {
  margin-top: 2px;
  cursor: pointer;
}

.terms-link {
  color: #1a7fd4;
  text-decoration: none;
  transition: color 0.2s;
}

.terms-link:hover {
  color: #0d6ebd;
  text-decoration: underline;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary {
  background-color: #1a7fd4;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0d6ebd;
}

.btn-primary:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.btn-block {
  width: 100%;
}

.auth-footer {
  margin-top: 24px;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

.auth-link {
  color: #1a7fd4;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
}

.auth-link:hover {
  color: #0d6ebd;
  text-decoration: underline;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@media (max-width: 640px) {
  .auth-modal-container {
    width: 95%;
    margin: 16px;
  }
  
  .auth-modal-header,
  .auth-form {
    padding: 20px;
  }
  
}
</style>